import { APP_INITIALIZER, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { StoreComponent } from './components/store/store.component';
import { Api } from './services/api';
import { Globals } from './services/globals';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BarcodeTesterComponent } from './components/barcode-tester/barcode-tester.component';
import { BarcodeGeneratorComponent } from './components/barcode-generator/barcode-generator.component';
import { LatestOrdersComponent } from './components/latest-orders/latest-orders.component';
import { GoogleCloudUploaderComponent } from './components/google-cloud-uploader/google-cloud-uploader.component';
import { FileUploadModule } from 'ng2-file-upload';
import { PrintFilesComponent } from './components/print-files/print-files.component';
import { StoreAdminPanelComponent } from './components/panel/admin-panel.component';
import { PostersComponent } from './components/panel/posters/posters.component';
import { PosterComponent } from './components/panel/posters/poster.component';
import { FileUploaderComponent } from './components/shared/file-uploader/file-uploader.component';
import { ImageGeneratorComponent } from './components/image-generator/image-generator.component';
import { PosterOrdersComponent } from './components/panel/poster-orders/poster-orders.component';
import { SizePriceEditorComponent } from './components/shared/size-price-editor/size-price-editor.component';
import { StoreSettingsComponent } from './components/panel/store-settings/store-settings.component';
import { AdminPanelComponent } from './components/admin/admin-panel.component';
import { Utility } from './services/utility';
import { AdminSettingsComponent } from './components/admin/settings/admin-settings.component';
import { StoresComponent } from './components/admin/stores/stores.component';
import { PictufyOverviewComponent } from './components/admin/pictufy/pictufy-overview.component';
import { NgChartsModule } from 'ng2-charts';
import { DashboardComponent } from './components/admin/dashboard/dashboard.component';
import { MatChipsModule } from '@angular/material/chips';
import { PrintFileComponent } from './components/print-files/print-file.component';
import { CreditTransactionsComponent } from './components/panel/credit-transactions/credit-transactions.component';
import { AdminNotificationsComponent } from './components/admin/notifications/admin-notifications.component';
import { AllPosterOrdersComponent } from './components/admin/poster-orders/all-poster-orders.component';
import { CreateOrderComponent } from './components/panel/create-order/create-order.component';
import { LoginComponent } from './components/login/login.component';
import { ApiDocumentationComponent } from './components/api-documentation/api-documentation.component';
import { HelpComponent } from './components/panel/help/help.component';
import { OrderItemAdministrationComponent } from './components/panel/poster-orders/order-item-administration/order-item-administration.component';
import { PosterOrderComponent } from './components/panel/poster-orders/poster-order/poster-order.component';
import { StoreHomeComponent } from './components/panel/home/home.component';
import { AllReclaimsComponent } from './components/admin/reclaims/all-reclaims.component';
import { AdminOverviewComponent } from './components/admin/overview/admin-overview.component';
import { ProductOverviewComponent } from './components/panel/product-overview/product-overview.component';
import { PriceListComponent } from './components/panel/price-list/price-list.component';
import { AdminUsersComponent } from './components/admin/users/admin-users.component';
import { TranslationService } from './services/translation';
import { TranslatePipe } from './services/translate.pipe';
import { GenerateArtworksComponent } from './components/panel/generate-artworks/generate-artworks.component';

export function loadTranslations(translations: TranslationService) {
    return () => translations.load();
}

@NgModule({ declarations: [
        AppComponent,
        StoreComponent,
        BarcodeTesterComponent,
        BarcodeGeneratorComponent,
        LatestOrdersComponent,
        GoogleCloudUploaderComponent,
        PrintFilesComponent,
        StoreAdminPanelComponent,
        PostersComponent,
        PosterComponent,
        FileUploaderComponent,
        ImageGeneratorComponent,
        PosterOrdersComponent,
        SizePriceEditorComponent,
        StoreSettingsComponent,
        AdminPanelComponent,
        AdminSettingsComponent,
        StoresComponent,
        PictufyOverviewComponent,
        DashboardComponent,
        PrintFileComponent,
        CreditTransactionsComponent,
        AdminNotificationsComponent,
        AllPosterOrdersComponent,
        CreateOrderComponent,
        LoginComponent,
        ApiDocumentationComponent,
        HelpComponent,
        OrderItemAdministrationComponent,
        PosterOrderComponent,
        StoreHomeComponent,
        AllReclaimsComponent,
        AdminOverviewComponent,
        ProductOverviewComponent,
        PriceListComponent,
        AdminUsersComponent,
        GenerateArtworksComponent,
        TranslatePipe
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        FormsModule,
        MatButtonModule,
        MatInputModule,
        MatFormFieldModule,
        MatCardModule,
        MatTableModule,
        MatCheckboxModule,
        MatSelectModule,
        MatIconModule,
        MatTabsModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatTooltipModule,
        MatDialogModule,
        MatSliderModule,
        MatRadioModule,
        MatSlideToggleModule,
        MatSidenavModule,
        MatChipsModule,
        MatMenuModule,
        MatExpansionModule,
        FileUploadModule,
        NgChartsModule], providers: [
        Api,
        Globals,
        Utility,
        TranslationService,
        {
            provide: APP_INITIALIZER,
            useFactory: loadTranslations,
            deps: [TranslationService],
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
